import { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import cleanseAmenities from '../../../../../helpers/cleanseAmenities';
import {
  baselineAmenityKeywords,
  highSignalAmenityKeywords,
} from '../../../../../constants/HotelAmenityKeywords';

const HotelAmenities = ({
  amenities,
  starRating,
  doCleanseAmenities,
  doHideAmenitiesBelowHotelName,
}) => {
  const cleansedAmenities = useMemo(
    () => (doCleanseAmenities ? cleanseAmenities(amenities || []) : amenities || []),
    [amenities, doCleanseAmenities],
  );

  const allUniqueAmenities = useMemo(() => {
    const uniqueAmenities = new Map();

    (cleansedAmenities || []).forEach((amenity, index) => {
      if (typeof amenity !== 'string') return;

      const normalizedAmenity = amenity.trim();
      if (!normalizedAmenity) return;

      const lowerAmenity = normalizedAmenity.toLowerCase();

      if (uniqueAmenities.has(lowerAmenity)) return;

      uniqueAmenities.set(lowerAmenity, {
        amenity: normalizedAmenity,
        index,
      });
    });

    return Array.from(uniqueAmenities.values())
      .sort((a, b) => a.index - b.index)
      .map((item) => item.amenity);
  }, [cleansedAmenities]);

  const amenitiesFilteredList = useMemo(() => {
    const uniqueAmenityList = (allUniqueAmenities || []).map((amenity, index) => {
      const lowerAmenity = amenity.toLowerCase();
      const isBaseline = baselineAmenityKeywords.some((keyword) => lowerAmenity.includes(keyword));
      const keywordPriority = highSignalAmenityKeywords.findIndex((keyword) =>
        lowerAmenity.includes(keyword),
      );

      return {
        amenity,
        isBaseline,
        keywordPriority,
        index,
      };
    });

    const highSignalAmenities = uniqueAmenityList
      .filter((item) => item.keywordPriority > -1 && !item.isBaseline)
      .sort(
        (a, b) =>
          a.keywordPriority - b.keywordPriority ||
          a.amenity.localeCompare(b.amenity) ||
          a.index - b.index,
      )
      .map((item) => item.amenity);

    if (highSignalAmenities.length > 0) {
      return highSignalAmenities;
    }

    const fallbackNonBaseline = uniqueAmenityList
      .filter((item) => !item.isBaseline)
      .sort((a, b) => a.index - b.index)
      .slice(0, 4)
      .map((item) => item.amenity);

    if (fallbackNonBaseline.length > 0) {
      return fallbackNonBaseline;
    }

    return uniqueAmenityList
      .sort((a, b) => a.index - b.index)
      .slice(0, 4)
      .map((item) => item.amenity);
  }, [allUniqueAmenities]);

  const previewAmenities = useMemo(
    () => (amenitiesFilteredList || []).slice(0, 4),
    [amenitiesFilteredList],
  );

  if (doHideAmenitiesBelowHotelName) {
    return null;
  }

  return (
    <div className="text-sm mt-2 text-gray-700">
      <div>
        <p className="uppercase">Featured Amenities:</p>

        {starRating > 0 && <span>{`${starRating}-star hotel`}</span>}

        {previewAmenities?.length > 0 && (
          <span>
            {starRating > 0 ? ' • ' : ''}
            {previewAmenities.join(' • ')}
          </span>
        )}
      </div>
    </div>
  );
};

HotelAmenities.propTypes = {
  amenities: PropTypes.arrayOf(PropTypes.string),
  starRating: PropTypes.number,
  doCleanseAmenities: PropTypes.bool,
  doHideAmenitiesBelowHotelName: PropTypes.bool,
};

HotelAmenities.defaultProps = {
  amenities: [],
  starRating: 0,
  doCleanseAmenities: true,
  doHideAmenitiesBelowHotelName: false,
};

export default memo(HotelAmenities);
